import { useEffect, useMemo, useState } from "react";
import { getJson, nf, ago, fmtDate, card, control, sectionLabel, entityPath, Chip, ConfidenceBadge, CATEGORY_BY_SLUG, TYPE_LABEL } from "./shared";
import { StixExportLink, StixTypeTag, queryOf } from "./Stix";

// Published threat reports (reports.js on the server): the Reports list, a report
// card, a report's indicators, and the "Reported in" tab on other profiles.

const PAGE = 30;
const PERIODS = [["", "Any time"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]];
const HAS = [["", "Anything"], ["indicators", "Indicators"], ["cves", "CVEs"], ["groups", "Threat groups"], ["software", "Malware or tools"], ["techniques", "ATT&CK technique IDs"], ["wallets", "Cryptocurrency wallets"]];
const SORTS = [["published", "Newest"], ["indicators", "Most indicators"]];
const IOC_LABEL = { "ipv4-addr": "IP", "domain-name": "Domain", url: "URL", file: "Hash" };

function countsLine(c = {}) {
  const parts = [];
  if (c.indicators) parts.push(`${nf(c.indicators)} indicator${c.indicators === 1 ? "" : "s"}`);
  if (c.vulnerabilities) parts.push(`${nf(c.vulnerabilities)} CVE${c.vulnerabilities === 1 ? "" : "s"}`);
  if (c.techniques) parts.push(`${nf(c.techniques)} technique${c.techniques === 1 ? "" : "s"}`);
  if (c.groups) parts.push(`${nf(c.groups)} group${c.groups === 1 ? "" : "s"}`);
  if (c.software) parts.push(`${nf(c.software)} malware/tool${c.software === 1 ? "" : "s"}`);
  return parts.join(" · ") || "Nothing extracted";
}

export function ReportCard({ s, ui, extra }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const color = getConf("report").color;
  return (
    <a href={entityPath(s)} style={{ display: "flex", flexDirection: "column", gap: 8, textDecoration: "none", ...card(ui), padding: 16, minWidth: 0 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: fs(11), color: B.textDim, flexWrap: "wrap" }}>
        <TypeIcon type="report" color={color} size={14} />
        <span style={{ color, fontWeight: 700 }}>{s.source?.name}</span>
        <span>·</span>
        <span title={fmtDate(s.published) || ""}>{ago(s.published)}</span>
        {extra && <span style={{ marginLeft: "auto" }}>{extra}</span>}
      </div>
      <div style={{ fontSize: fs(15), fontWeight: 800, color: B.text, lineHeight: 1.35 }}>{s.name}</div>
      <div style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{s.snippet}</div>
      {s.mentioned?.length > 0 && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {s.mentioned.slice(0, 6).map(m => <Chip key={m.id} ui={ui} color={getConf(m.type).color}>{m.name}</Chip>)}
          {s.mentioned.length > 6 && <Chip ui={ui}>+{s.mentioned.length - 6}</Chip>}
        </div>
      )}
      <div style={{ marginTop: "auto", fontSize: fs(11), color: B.textDim }}>{countsLine(s.counts)}</div>
    </a>
  );
}

export function KbReportList({ meta, ui, initialMentions, initialSource }) {
  const { B, fs, getConf } = ui;
  const cat = CATEGORY_BY_SLUG.reports;
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [source, setSource] = useState(initialSource || "");
  const [days, setDays] = useState("");
  const [has, setHas] = useState("");
  const [sort, setSort] = useState("published");
  const [mentions, setMentions] = useState(initialMentions || "");
  const [pages, setPages] = useState([]);
  const [state, setState] = useState({ loading: true, error: "", total: 0, info: null, subject: null });

  useEffect(() => { setMentions(initialMentions || ""); }, [initialMentions]);
  useEffect(() => { const t = setTimeout(() => setQuery(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const base = useMemo(() => {
    const u = new URLSearchParams({ sort });
    if (query) u.set("q", query);
    if (source) u.set("source", source);
    if (days) u.set("days", days);
    if (has) u.set("has", has);
    if (mentions) u.set("mentions", mentions);
    return `/api/kb/reports?${u}`;
  }, [query, source, days, has, mentions, sort]);

  useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    // Fresh each time: reports arrive one by one while the server reads them.
    getJson(`${base}&offset=0&limit=${PAGE}`, { fresh: true })
      .then(d => { if (!live) return; setPages([d.items]); setState({ loading: false, error: "", total: d.total, info: d.state, subject: d.subject }); })
      .catch(e => live && setState(s => ({ ...s, loading: false, error: e.message })));
    return () => { live = false; };
  }, [base]);

  const items = pages.flat();
  const more = () => getJson(`${base}&offset=${items.length}&limit=${PAGE}`, { fresh: true }).then(d => setPages(p => [...p, d.items])).catch(() => {});
  const info = state.info || meta?.reports;
  const feeds = info?.feeds || [];
  const reading = info && (info.refreshing || info.pending > 0);

  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "4px 0 6px" }}>
        <h2 style={{ fontSize: fs(22), fontWeight: 900, margin: 0, color: B.text }}>{cat.label}</h2>
        <span style={{ fontSize: fs(13), color: getConf(cat.type).color, fontWeight: 800 }}>{info?.count ? nf(info.count) : ""}</span>
        <StixTypeTag ui={ui} type="report" />
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 4px", maxWidth: "110ch", lineHeight: 1.6 }}>
        The newest reports from {feeds.length ? `${feeds.length} security vendors and agencies (listed in the source menu below)` : "security vendors and agencies"}. Each is read by keyword rules, not AI: indicators (with the ones the author defanged marked), CVEs, ATT&CK technique IDs, and the groups and malware the Knowledge Base knows. Open one for its summary, what it names and how they connect, and its STIX 2.1 export. Only summaries are shown here; the full article is on the publisher's site.
      </p>
      <div style={{ fontSize: fs(11), color: B.textDim, margin: "0 0 14px" }}>
        {info?.builtAt ? `Feeds read ${ago(info.builtAt)}` : "Reading the feeds…"}{reading ? ` · reading ${info.pending ? `${nf(info.pending)} more articles` : "articles"}…` : ""}
        {" · "}<a href="#/kb/confidence" style={{ color: B.textDim }}>How we score confidence</a>
      </div>

      {state.subject && (
        <div style={{ ...card(ui), padding: "10px 14px", marginBottom: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: fs(12), color: B.textMuted }}>
          <span>Reports that name</span>
          <a href={entityPath(state.subject)} style={{ textDecoration: "none" }}><Chip ui={ui} color={getConf(state.subject.type).color}>{state.subject.name}</Chip></a>
          <span style={{ color: B.textDim }}>{TYPE_LABEL[state.subject.type] || ""}</span>
          <button onClick={() => { setMentions(""); window.history.replaceState(null, "", "#/kb/reports"); }} style={{ marginLeft: "auto", background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}>Show all reports</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Keywords: a group, malware, CVE, IP, domain, hash or any word in the article…" aria-label="Search reports" style={{ ...control(ui), flex: "1 1 280px", minWidth: 0 }} />
        <select value={source} onChange={e => setSource(e.target.value)} aria-label="Source" style={control(ui)}>
          <option value="">All sources</option>
          {[...feeds].sort((a, b) => a.name.localeCompare(b.name)).map(f => <option key={f.key} value={f.key}>{f.name}</option>)}
        </select>
        <select value={days} onChange={e => setDays(e.target.value)} aria-label="Published" style={control(ui)}>
          {PERIODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={has} onChange={e => setHas(e.target.value)} aria-label="Contains" style={control(ui)}>
          {HAS.map(([v, l]) => <option key={v} value={v}>{v ? `Contains: ${l}` : "Contains: anything"}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Sort" style={control(ui)}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
        <StixExportLink ui={ui} href={`/api/kb/stix/reports${queryOf({ q: query, source, days, has, mentions })}`} title="Download these reports as STIX 2.1: each report with the groups, malware, techniques and CVEs it names and the indicators it lists (up to 8,000 objects)" />
      </div>
      <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 10 }}>{state.loading ? "Loading…" : `${nf(state.total)} report${state.total === 1 ? "" : "s"}`}</div>
      {state.error && <div style={{ color: "#dc2626", fontSize: fs(12), marginBottom: 10 }}>{state.error}</div>}
      {!state.loading && !items.length && (
        <div style={{ ...card(ui), padding: "16px 18px", fontSize: fs(13), color: B.textMuted }}>
          {info?.count ? "No reports match these filters." : "No reports yet. The server reads the feeds a few minutes after it starts; check back shortly."}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))", gap: 12 }}>
        {items.map(s => <ReportCard key={s.id} s={s} ui={ui} />)}
      </div>
      {items.length < state.total && (
        <button onClick={more} style={{ ...control(ui), cursor: "pointer", marginTop: 14 }}>Show {Math.min(PAGE, state.total - items.length)} more</button>
      )}
    </>
  );
}

// A report's indicators, as extracted. Values are text, never links.
// vendor: the publisher's own indicator file for this report, when one was found (vendor-iocs.js).
export function ReportIocTable({ iocs, ui, vendor }) {
  const { B, fs, HP } = ui;
  const [type, setType] = useState("");
  const [onlyDefanged, setOnlyDefanged] = useState(false);
  const [onlyVendor, setOnlyVendor] = useState(false);
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);
  const types = useMemo(() => [...new Set(iocs.map(i => i.type))], [iocs]);
  const rows = iocs.filter(i => (!type || i.type === type) && (!onlyDefanged || i.defanged) && (!onlyVendor || i.vendor) && (!q || i.value.toLowerCase().includes(q.toLowerCase())));
  const copy = () => navigator.clipboard?.writeText(rows.map(i => i.value).join("\n")).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  const defangedCount = iocs.filter(i => i.defanged && !i.vendor).length;
  const VENDOR = "#16a34a";

  return (
    <>
      {vendor && (
        <div style={{ ...card(ui), padding: "12px 16px", marginBottom: 12, borderColor: VENDOR + "66", fontSize: fs(12), color: B.textMuted, lineHeight: 1.6 }}>
          <b style={{ color: B.text }}>{vendor.name}'s own indicator file for this report</b> lists {nf(vendor.listed)} indicator{vendor.listed === 1 ? "" : "s"}{vendor.alsoInText ? `, ${nf(vendor.alsoInText)} of them also in the article's text` : ""}.
          They're marked below and score higher: the publisher listed them on purpose, while the article's text also links to tools and references.{" "}
          <a href={vendor.url} target="_blank" rel="noreferrer noopener" style={{ color: B.accent }}>The file on GitHub ↗</a>
          <span style={{ color: B.textDim }}> · matched by {vendor.matchedBy === "link" ? "the article's address" : vendor.matchedBy === "name" ? "its date and name" : "its title"}{vendor.license ? ` · ${vendor.license}` : ""}</span>
        </div>
      )}
      <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 12px", lineHeight: 1.6, maxWidth: "110ch" }}>
        {vendor ? "The rest were found in the article's text." : "Indicators found in the article's text."} {defangedCount ? `${nf(defangedCount)} were defanged by the author (written like evil[.]com so they can't be clicked), the clearest sign they're malicious.` : "None were defanged by the author."} Plain-text domains and URLs get low confidence: articles also link to vendors, tools and references. Values are shown as text and never linked; they may be live malware.
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10, alignItems: "center" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter values…" aria-label="Filter indicators" style={{ ...control(ui), flex: "1 1 220px", minWidth: 0 }} />
        <select value={type} onChange={e => setType(e.target.value)} aria-label="Type" style={control(ui)}>
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{IOC_LABEL[t] || t}</option>)}
        </select>
        <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: fs(12), color: B.textMuted, cursor: "pointer" }}>
          <input type="checkbox" checked={onlyDefanged} onChange={e => setOnlyDefanged(e.target.checked)} /> Defanged only
        </label>
        {vendor && (
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: fs(12), color: B.textMuted, cursor: "pointer" }}>
            <input type="checkbox" checked={onlyVendor} onChange={e => setOnlyVendor(e.target.checked)} /> In {vendor.name}'s file only
          </label>
        )}
        <button onClick={copy} disabled={!rows.length} style={{ ...HP.navBtn, padding: "7px 13px", fontSize: fs(12) }}>{copied ? "Copied ✓" : `Copy ${nf(rows.length)}`}</button>
      </div>
      <div style={{ ...card(ui), overflow: "hidden" }}>
        {rows.length ? rows.map((i, n) => (
          <div key={`${i.type}|${i.value}`} className="kb-row" style={{ display: "flex", gap: 12, padding: "9px 14px", borderTop: n ? `1px solid ${B.border}` : "none", alignItems: "center", flexWrap: "wrap", fontSize: fs(12) }}>
            <span style={{ width: 54, flexShrink: 0, color: B.textDim, fontSize: fs(11) }}>{IOC_LABEL[i.type] || i.type}</span>
            <span style={{ flex: "1 1 260px", minWidth: 0, fontFamily: "monospace", color: B.text, wordBreak: "break-all" }}>{i.value}</span>
            {i.vendor && <Chip ui={ui} color={VENDOR} title={`Listed in ${vendor?.name || "the publisher"}'s own indicator file for this report`}>{vendor?.name || "vendor"}'s list</Chip>}
            {i.defanged && !i.vendor && <Chip ui={ui} color="#dc2626" title="The author wrote this defanged (e.g. evil[.]com)">defanged</Chip>}
            {i.count > 1 && <span style={{ color: B.textDim }}>×{i.count}</span>}
            {i.inFeeds && <a href={`#/kb/e/${encodeURIComponent(i.value)}`} style={{ color: B.accent, textDecoration: "none", fontSize: fs(11) }}>Also in the feeds →</a>}
            <ConfidenceBadge confidence={i.confidence} ui={ui} />
          </div>
        )) : <div style={{ padding: 16, color: B.textDim, fontSize: fs(12) }}>No indicators match.</div>}
      </div>
    </>
  );
}

// What our rules connected inside a report: "<group> uses <malware>", and so on. A
// report published on Idle Hour (stated) shows the links its author drew instead.
export function ReportLinks({ derived, ui, stated }) {
  const { B, fs, getConf } = ui;
  if (!derived?.length) return null;
  return (
    <div>
      <div style={{ ...sectionLabel(ui), marginBottom: 8 }}>{stated ? "Links stated in this report" : "Relationships in this report"} ({nf(derived.length)})</div>
      <div style={{ ...card(ui) }}>
        {derived.slice(0, 40).map((d, i) => (
          <div key={i} style={{ display: "flex", gap: 8, padding: "10px 16px", borderTop: i ? `1px solid ${B.border}` : "none", alignItems: "center", flexWrap: "wrap", fontSize: fs(13) }}>
            <a href={entityPath(d.from)} style={{ color: getConf(d.from.type).color, fontWeight: 700, textDecoration: "none" }}>{d.from.name}</a>
            <span style={{ color: B.textDim, fontStyle: "italic" }}>{d.verb}</span>
            <a href={entityPath(d.to)} style={{ color: getConf(d.to.type).color, fontWeight: 700, textDecoration: "none" }}>{d.to.attackId && d.to.type === "attack-pattern" ? `${d.to.attackId} ` : ""}{d.to.name}</a>
            <span style={{ marginLeft: "auto" }}><ConfidenceBadge confidence={d.confidence} ui={ui} /></span>
          </div>
        ))}
      </div>
      {stated ? (
        <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6, lineHeight: 1.55 }}>
          {derived[0].note}: the author's own claims, credited to them in the STIX, not inferred by rules.
          {derived.length > 40 ? ` Showing 40 of ${nf(derived.length)}; the STIX export has them all.` : ""}
        </div>
      ) : (
      <div style={{ fontSize: fs(11), color: B.textDim, marginTop: 6, lineHeight: 1.55 }}>
        Inferred by rules: when a report is mainly about one group or malware family (the only one it names, at least twice, or one named far more often than the rest), it's linked to the other software, techniques, CVEs and indicators the report names. The report may not state each link, so they're all Low confidence.
        {derived.length > 40 ? ` Showing 40 of ${nf(derived.length)}; the STIX export has them all.` : ""}
      </div>
      )}
    </div>
  );
}

// "Reported in" on any other profile.
export function ReportedInTab({ reports, subject, ui }) {
  const { B, fs } = ui;
  const key = subject.type === "vulnerability" ? subject.name : subject.value || subject.attackId || subject.id;
  return (
    <>
      <p style={{ fontSize: fs(12), color: B.textMuted, margin: "0 0 12px", lineHeight: 1.6, maxWidth: "110ch" }}>
        Recent published reports that name {subject.name || subject.value}, found by keyword rules. A mention isn't always about it: open a report to see how often and how it's named.{" "}
        <a href={`#/kb/reports?mentions=${encodeURIComponent(key)}`} style={{ color: B.accent, textDecoration: "none" }}>Search and filter these →</a>
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))", gap: 12 }}>
        {reports.map(r => <ReportCard key={r.id} s={r} ui={ui} extra={r.mentionCount > 1 ? `named ${nf(r.mentionCount)}×` : null} />)}
      </div>
    </>
  );
}
